import React, { useState, useRef } from 'react';
import { Item } from '../types';
import './ItemForm.css';

interface ItemFormProps {
  onSubmit: (item: Partial<Item>) => void;
  initialValues?: Partial<Item>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

// Base64 encoded simple SVG placeholder
const PLACEHOLDER_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNGNUY1RjUiLz48cGF0aCBkPSJNODAgNjBIMTIwVjE0MEg4MFY2MFoiIGZpbGw9IiNEREQiLz48cGF0aCBkPSJNNjUgOTBIMTM1VjExMEg2NVY5MFoiIGZpbGw9IiNEREQiLz48L3N2Zz4=';

const ItemForm: React.FC<ItemFormProps> = ({ onSubmit, initialValues, onCancel, isSubmitting }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    name: initialValues?.name || '',
    description: initialValues?.description || '',
    dailyRateGBP: initialValues?.dailyRateGBP || 10,
    minDays: initialValues?.minDays || 1,
    maxDays: initialValues?.maxDays || 7,
    category: initialValues?.category || '',
    images: initialValues?.images || [],
    location: initialValues?.location || {
      address: '',
      city: '',
      postcode: '',
      coordinates: {
        latitude: 0,
        longitude: 0
      }
    },
    availability: initialValues?.availability ?? true,
    rentalPeriods: initialValues?.rentalPeriods || []
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('location.')) {
      const locationField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        location: {
          ...prev.location!,
          [locationField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'dailyRateGBP' || name === 'minDays' || name === 'maxDays' 
          ? parseFloat(value) 
          : value
      }));
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, reader.result as string]
        }));
      };
      reader.readAsDataURL(file);
    });

    // Reset the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="item-form">
      <div className="form-group">
        <label htmlFor="name">Item Name</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="description">Description</label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group">
        <label>Images</label>
        <div className="images-container">
          {formData.images.map((image, index) => (
            <div key={index} className="image-preview">
              <img src={image} alt={`Item preview ${index + 1}`} />
              <button
                type="button"
                onClick={() => handleRemoveImage(index)}
                className="remove-image"
                aria-label="Remove image"
              >
                ×
              </button>
            </div>
          ))}
          <div className="add-image-button">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              ref={fileInputRef}
              style={{ display: 'none' }}
              multiple
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="upload-button"
            >
              + Add Image
            </button>
          </div>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="dailyRateGBP">Daily Rate (£)</label>
        <input
          type="number"
          id="dailyRateGBP"
          name="dailyRateGBP"
          value={formData.dailyRateGBP}
          onChange={handleChange}
          step="0.01"
          min="0"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="category">Category</label>
        <select
          id="category"
          name="category"
          value={formData.category}
          onChange={handleChange}
          required
        >
          <option value="">Select a category</option>
          <option value="Garden Tools">Garden Tools</option>
          <option value="Power Tools">Power Tools</option>
          <option value="Hand Tools">Hand Tools</option>
          <option value="Outdoor Equipment">Outdoor Equipment</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="minDays">Minimum Days</label>
          <input
            type="number"
            id="minDays"
            name="minDays"
            value={formData.minDays}
            onChange={handleChange}
            min="1"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="maxDays">Maximum Days</label>
          <input
            type="number"
            id="maxDays"
            name="maxDays"
            value={formData.maxDays}
            onChange={handleChange}
            min={formData.minDays}
            required
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="location.address">Address</label>
        <input
          type="text"
          id="location.address"
          name="location.address"
          value={formData.location?.address}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="location.city">City</label>
        <input
          type="text"
          id="location.city"
          name="location.city"
          value={formData.location?.city}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="location.postcode">Postcode</label>
        <input
          type="text"
          id="location.postcode"
          name="location.postcode"
          value={formData.location?.postcode}
          onChange={handleChange}
          required
        />
        <small className="form-help">
          Your address will be validated and coordinates will be automatically filled in for map display.
        </small>
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="cancel-button">
          Cancel
        </button>
        <button type="submit" className="submit-button" disabled={isSubmitting}>
          {isSubmitting ? 'Adding...' : (initialValues ? 'Update Item' : 'Add Item')}
        </button>
      </div>
    </form>
  );
};

export default ItemForm; 